import React from "react";
import { icons } from "../../../svg";
import {
  FreightRail,
  IDestinationExtension,
  IOriginExtension,
} from "../../../types/IQuotes";

interface RailRouteLocationsDetailsProps {
  origin: IOriginExtension;
  freight: FreightRail;
  destination: IDestinationExtension;
}

const RailRouteLocationsDetails: React.FC<RailRouteLocationsDetailsProps> = ({
  origin,
  freight,
  destination,
}) => {
  // TODO: error handling for missing legs or missing data in legs
  // The first leg in the freight.legs array corresponds to the origin,
  // and the last leg corresponds to the destination.
  // Any legs in between are interchanges.
  const firstLeg = freight.legs[0];
  const lastLeg = freight.legs[freight.legs.length - 1];
  const interchanges = freight.legs.slice(1);

  return (
    <div className="route-details-locations">
      {/* Origin Section */}
      <div className="location-container">
        <div className="location-left-column">
          <div className="rotated-text-container">
            <span className="rotated-text">
              {firstLeg?.carrier}
              <span className="space-between" />
              {String(firstLeg?.miles)} mi
            </span>
          </div>
        </div>
        <div className="location-middle-column">
          <img
            data-tooltip-id="origin"
            src={icons.OriginIcon}
            alt="OriginIcon"
          />
          <span className="padding-top-5">
            <img
              className="start-arrow-icon"
              data-tooltip-id="down-arrow"
              src={icons.DownArrowIcon}
              alt="DownArrowIcon"
            />
          </span>
        </div>
        <div className="location-right-column">
          <div className="section-border pb-4">
            <div className="flex items-center mb-1">
              <span className="freight-detail-location-header padding-left-10">
                Origin: {origin?.name}
              </span>
            </div>
            <div className="padding-left-20 origin-container">
              <div className="padding-top-10">
                <span className="route-detail-equip-label-station">
                  STATION:{" "}
                </span>{" "}
                <span
                  id="origin-station-state"
                  className="route-detail-equip-value"
                >
                  {freight.originStation || ""}
                </span>
              </div>
              <div className="origin-detail-container padding-top-5">
                <div>
                  <div>
                    <span className="route-detail-equip-label">
                      Serving Carrier:
                    </span>
                    <span className="serving-carrier-value">
                      {firstLeg?.originCarrier}
                    </span>
                  </div>
                </div>
                <div>
                  <span>
                    <img
                      data-tooltip-id="VerticalBar"
                      src={icons.VerticalBarIcon}
                      alt="VerticalBar"
                      className="icon"
                    />
                  </span>
                </div>
                <div>
                  <div>
                    <span className="route-detail-equip-label">
                      Railroad Doc:
                    </span>
                    <span className="route-detail-equip-value padding-left-5">
                      {firstLeg?.document}
                    </span>
                  </div>
                </div>
              </div>
              <div className="cost-details-container marging-top-10">
                <div className="route-detail-cost-details-row">
                  <div className="equip-content-group">
                    <span>
                      <img
                        data-tooltip-id="DollarSign"
                        src={icons.DollarSignIcon}
                        alt="DollarSignIcon"
                        className="icon"
                      />
                    </span>
                    <span className="route-detail-equip-label">Base rate</span>
                  </div>
                  <span className="route-detail-equip-value">
                    {(firstLeg?.rate ?? 0).toFixed(2)}
                  </span>
                </div>
                <div className="route-detail-cost-details-row">
                  <div className="equip-content-group">
                    <span className="padding-left-2">
                      <img
                        data-tooltip-id="FuelPump"
                        src={icons.FuelPumpIcon}
                        alt="FuelPumpIcon"
                        className="icon"
                      />
                    </span>
                    <span className="route-detail-equip-label">Fuel</span>
                  </div>
                  <span className="route-detail-equip-value">
                    {firstLeg?.fsc}
                  </span>
                </div>
                <div className="route-detail-cost-details-row highlight">
                  <div className="equip-content-group">
                    <span className="padding-left-3">
                      <img
                        data-tooltip-id="TotalDolloar"
                        src={icons.TotalDolloarIcon}
                        alt="TotalDolloarIcon"
                        className="icon"
                      />
                    </span>
                    <span className="route-detail-equip-label">
                      Equip Total Cost
                    </span>
                  </div>
                  <span className="route-detail-equip-value">
                    {((firstLeg?.rate ?? 0) + (firstLeg?.fsc ?? 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interchange Section */}
      {interchanges.length > 0 ? (
        interchanges.map((leg) => (
          <div key={leg.document} className="location-container">
            <div className="location-left-column">
              <div className="rotated-text-container">
                <span className="rotated-text">
                  {leg.carrier}
                  <span className="space-between" />
                  {String(leg.miles)} mi
                </span>
              </div>
            </div>
            <div className="location-middle-column">
              <img
                data-tooltip-id="interchange"
                src={icons.InterchangeIcon}
                alt="InterchangeIcon"
              />
              <span className="padding-top-5">
                <img
                  data-tooltip-id="interchange-arrow"
                  src={icons.DownArrowIcon}
                  alt="DownArrowIcon"
                  className="interchange-arrow-icon"
                />
              </span>
            </div>
            <div className="location-right-column">
              <div className="section-border pb-4">
                <div className="flex items-center mb-1">
                  <span className="freight-detail-location-header padding-left-10">
                    Interchange: {leg.interchange}
                  </span>
                </div>
                <div className="padding-left-20 origin-container">
                  <div className="padding-top-5">
                    <span className="route-detail-equip-label">
                      Railroad Doc:{" "}
                    </span>{" "}
                    <span className="route-detail-equip-value">
                      {leg.document}
                    </span>
                  </div>
                  <div className="cost-details-container marging-top-10">
                    <div className="route-detail-cost-details-row">
                      <div className="equip-content-group">
                        <span>
                          <img
                            data-tooltip-id="DollarSign"
                            src={icons.DollarSignIcon}
                            alt="DollarSignIcon"
                            className="icon"
                          />
                        </span>
                        <span className="route-detail-equip-label">
                          Base rate
                        </span>
                      </div>
                      <span className="route-detail-equip-value">
                        {(leg.rate ?? 0).toFixed(2)}
                      </span>
                    </div>

                    <div className="route-detail-cost-details-row">
                      <div className="equip-content-group">
                        <span className="padding-left-2">
                          <img
                            data-tooltip-id="FuelPump"
                            src={icons.FuelPumpIcon}
                            alt="FuelPumpIcon"
                            className="icon"
                          />
                        </span>
                        <span className="route-detail-equip-label">Fuel</span>
                      </div>
                      <span className="route-detail-equip-value">
                        {leg.fsc}
                      </span>
                    </div>

                    <div className="route-detail-cost-details-row highlight">
                      <div className="equip-content-group">
                        <span className="padding-left-3">
                          <img
                            data-tooltip-id="TotalDolloar"
                            src={icons.TotalDolloarIcon}
                            alt="TotalDolloarIcon"
                            className="icon"
                          />
                        </span>
                        <span className="route-detail-equip-label">
                          Equip Total Cost
                        </span>
                      </div>
                      <span className="route-detail-equip-value">
                        {((leg.rate ?? 0) + (leg.fsc ?? 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div />
      )}

      {/* Destination Section */}
      <div className="location-container">
        <div className="location-left-column" />
        <div className="location-middle-column">
          <img
            data-tooltip-id="destination"
            src={icons.DestinationIcon}
            alt="DestinationIcon"
          />
        </div>
        <div className="location-right-column">
          <div className="pb-4">
            <div className="flex items-center mb-1">
              <span className="freight-detail-location-header padding-left-10 Destination-header padding-top-10">
                Destination: {destination.name}
              </span>
            </div>
            <div className="padding-left-20 origin-container">
              <div className="padding-top-5">
                <span className="route-detail-equip-label-station">
                  STATION:
                </span>{" "}
                <span
                  id="destination-station-state"
                  className="route-detail-equip-value"
                >
                  {freight.destinationStation || ""}
                </span>
              </div>
              <div className="padding-top-5">
                <span className="route-detail-equip-label">
                  Serving Carrier:
                </span>{" "}
                <span className="serving-carrier-value">
                  {lastLeg?.destinationCarrier}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RailRouteLocationsDetails;
